import React from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { MantineProvider } from "@mantine/core";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { usePostHog } from "posthog-js/react";
import { AutoLogout } from "shared/src/components/auto-logout";
import { DevFeedbackPlugin } from "shared/src/components/dev-feedback";
import { signOutUser } from "./sign-out";
import { AuthEvent } from "./data/auth-events";
import ProductLayout from "./components/product/layout/layout";
import productEligibilityRoutes from "./routes/product-eligibility-routes";
import accountRoutes from "./routes/account-routes";
import routesWithoutSignup from "./routes/routes-without-signup";
import appealManagementRoutes from "./routes/appeal-management-routes";
import QueryAuthGate from "./routes/query-auth-gate/query-auth-gate";
import ClaimableAuthenticatorV2 from "./components/shared/claimable-authenticator-v2";
import Page from "./components/product/layout/page";
import theme from "./theme";
import IdentifyUser from "./components/identify-user";
import AuthRouteTracker from "./components/auth-route-tracker";
import PathConstants from "./routes/path-constants";
import { MetaProvider } from "./contexts/meta-context";
import AppealManagementAuthGate from "./routes/appeal-management-auth-gate/appeal-management-auth-gate";
import PageViewTracker from "./components/page-view-tracker";
import { BreakingChangesReloadGuard } from "shared/src/components/breaking-changes-reload-guard/breaking-changes-reload-guard";

import "@aws-amplify/ui-react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      allowGuestAccess: true,
    },
  },
});

const ResumeAppeal = React.lazy(
  () => import("./pages/product/resume/resume-appeal"),
);

const Appeals = React.lazy(() => import("./pages/product/account/appeals"));
const ReportAppealResultPage = React.lazy(
  () => import("./pages/product/reporting-flow/report-appeal-result"),
);
const UpdateAppealStatus = React.lazy(
  () => import("./pages/update-appeal-status"),
);
const MagicLinkUpheldFollowUp = React.lazy(
  () => import("./pages/magic-link-upheld-follow-up"),
);
const NotFound = React.lazy(() => import("./pages/product/error/not-found"));
function AutoLogoutWrapper() {
  const { authStatus } = useAuthenticator();
  const posthog = usePostHog();

  const handleInactivityLogout = () => {
    posthog.capture(AuthEvent.InactivityLogout);
  };

  return (
    <AutoLogout
      onLogout={signOutUser}
      onInactivityLogout={handleInactivityLogout}
      isAuthenticated={authStatus === "authenticated"}
    />
  );
}

function App() {
  const router = createBrowserRouter([
    {
      element: (
        <>
          <PageViewTracker />
          <BreakingChangesReloadGuard />
          <Outlet />
        </>
      ),
      children: [
        {
          element: <ProductLayout />,
          errorElement: (
            <Page errorLoading heading={false} menuButton={false} />
          ),
          children: productEligibilityRoutes,
        },
        {
          element: (
            <ClaimableAuthenticatorV2 defaultTab="signIn">
              <ProductLayout />
            </ClaimableAuthenticatorV2>
          ),
          errorElement: (
            <Page errorLoading heading={false} menuButton={false} />
          ),
          children: accountRoutes,
        },
        {
          element: (
            <ClaimableAuthenticatorV2 defaultTab="signIn">
              <ProductLayout />
            </ClaimableAuthenticatorV2>
          ),
          errorElement: (
            <Page errorLoading heading={false} menuButton={false} />
          ),
          children: [
            {
              path: PathConstants.MagicLinkUpheldFollowUp,
              element: <MagicLinkUpheldFollowUp />,
            },
          ],
        },
        {
          element: (
            <ClaimableAuthenticatorV2 defaultTab="signIn" hideSignUp>
              <ProductLayout />
            </ClaimableAuthenticatorV2>
          ),
          errorElement: (
            <Page errorLoading heading={false} menuButton={false} />
          ),
          children: routesWithoutSignup,
        },
        {
          element: <AppealManagementAuthGate />,
          errorElement: (
            <Page errorLoading heading={false} menuButton={false} />
          ),
          children: appealManagementRoutes,
        },
        {
          element: <ProductLayout />,
          errorElement: (
            <Page errorLoading heading={false} menuButton={false} />
          ),
          children: [
            { path: PathConstants.ResumeAppeal, element: <ResumeAppeal /> },
            {
              path: PathConstants.Appeals,
              element: (
                <QueryAuthGate>
                  <Appeals />
                </QueryAuthGate>
              ),
            },
            {
              path: PathConstants.ReportAppealResult,
              element: (
                <QueryAuthGate>
                  <ReportAppealResultPage />
                </QueryAuthGate>
              ),
            },
          ],
        },
        {
          element: <ProductLayout />,
          errorElement: (
            <Page errorLoading heading={false} menuButton={false} />
          ),
          children: [
            {
              path: PathConstants.UpdateAppealStatus,
              element: <UpdateAppealStatus />,
            },
          ],
        },
        {
          element: <ProductLayout />,
          errorElement: (
            <Page errorLoading heading={false} menuButton={false} />
          ),
          children: [{ path: PathConstants.NotFound, element: <NotFound /> }],
        },
      ],
    },
  ]);

  return (
    <MantineProvider theme={theme}>
      <MetaProvider>
        <Authenticator.Provider>
          <RouterProvider router={router} />
          <IdentifyUser />
          <AuthRouteTracker />
          <AutoLogoutWrapper />
        </Authenticator.Provider>
        {import.meta.env.VITE_SENTRY_ENV !== "prod" && <DevFeedbackPlugin />}
      </MetaProvider>
    </MantineProvider>
  );
}

export default App;
